<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import type { EnumOption } from '@/composables/useEnumOptions'
import { idOf, matchesBlankRow, usePersistedRows } from '@/composables/usePersistedRows'
import { toNumber } from '@/utils/number'
import { requestJson } from '@/utils/postJson'
import ProjectCollateralController from '@controllers/Investors/ProjectCollateralController'
import { ulid } from 'ulid'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CollateralData = App.Data.EstateObject.CollateralData
import CollateralType = App.Enums.EstateObject.Loans.CollateralType

const props = defineProps<{
  projectId: string
  initialCollaterals: CollateralData[]
  collateralTypeOptions: EnumOption[]
}>()

const DEFAULT_COLLATERAL_TYPE: CollateralType = 'guarantee'

interface CollateralRow {
  key: string
  id: string | null
  collateral_type: CollateralType | null
  nominal_value: number | null
}

const { t, n } = useI18n()

const toRow = (collateral: CollateralData): CollateralRow => ({
  key: collateral.id ?? ulid(),
  id: collateral.id ?? null,
  collateral_type: collateral.collateral_type ?? DEFAULT_COLLATERAL_TYPE,
  nominal_value: toNumber(collateral.nominal_value),
})

const blankCollateral = (): CollateralRow => ({
  key: ulid(),
  id: null,
  collateral_type: DEFAULT_COLLATERAL_TYPE,
  nominal_value: null,
})

const toPayload = (row: CollateralRow) => ({
  collateral_type: row.collateral_type,
  nominal_value: row.nominal_value,
})

const collateralRoute = (id: string) => ({ project: props.projectId, collateral: id })

const { rows, reset, addDraft, removeRow, persistChanges, persistOnLeave } = usePersistedRows({
  toPayload,
  isDraft: matchesBlankRow(toPayload, blankCollateral),
  create: async (payload) =>
    idOf(await requestJson<CollateralData>('post', ProjectCollateralController.store(props.projectId).url, payload)),
  update: (id, payload) => requestJson('put', ProjectCollateralController.update(collateralRoute(id)).url, payload),
  destroy: (id) => requestJson('delete', ProjectCollateralController.destroy(collateralRoute(id)).url, undefined),
  reloadProps: ['collaterals', 'fieldStates', 'submissionGapReport'],
})

reset(props.initialCollaterals.map(toRow))

const total = computed(() => rows.value.reduce((sum, row) => sum + (row.nominal_value ?? 0), 0))

const setCell = <TKey extends keyof CollateralRow>(key: string, field: TKey, value: CollateralRow[TKey]): void => {
  rows.value = rows.value.map((row) => (row.key === key ? { ...row, [field]: value } : row))
}
</script>

<template>
  <section @focusout="persistOnLeave">
    <div class="mb-3 flex items-baseline justify-between gap-3">
      <h3 class="section-label section-label--sm">{{ t('title') }}</h3>
      <span v-if="rows.length" class="text-ink-3 text-xs tabular-nums">
        {{ t('total') }} <b class="text-ink">{{ n(total, 'euros') }}</b>
      </span>
    </div>

    <p v-if="!rows.length" class="text-ink-3 mb-3 text-sm">{{ t('empty') }}</p>

    <div v-for="row in rows" :key="row.key" class="mb-2 flex items-center gap-2">
      <FinestSelect
        class="flex-1"
        :model-value="row.collateral_type"
        :options="collateralTypeOptions"
        :aria-label="t('type')"
        @update:model-value="(value) => setCell(row.key, 'collateral_type', value as CollateralType | null)"
        @change="persistChanges" />

      <FinestInputNumber
        class="w-40"
        :model-value="row.nominal_value"
        mode="currency"
        :aria-label="t('nominalValue')"
        @update:model-value="(value) => setCell(row.key, 'nominal_value', value)" />

      <button
        type="button"
        class="text-ink-3 hover:text-danger hover:bg-danger-subtle cursor-pointer rounded-md p-2"
        :aria-label="t('remove')"
        @click="removeRow(row.key)">
        <i class="pi pi-trash text-[13px]" aria-hidden="true" />
      </button>
    </div>

    <FinestButton
      class="mt-1 border-dashed"
      variant="secondary"
      icon="pi pi-plus"
      :label="t('add')"
      @click="addDraft(blankCollateral())" />
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Zusatzsicherheiten",
    "empty": "Noch keine Zusatzsicherheit hinterlegt.",
    "add": "Zusatzsicherheit hinzufügen",
    "remove": "Sicherheit entfernen",
    "type": "Art der Sicherheit",
    "nominalValue": "Nominalwert",
    "total": "Summe"
  },
  "en": {
    "title": "Additional securities",
    "empty": "No additional security recorded yet.",
    "add": "Add additional security",
    "remove": "Remove security",
    "type": "Security type",
    "nominalValue": "Nominal value",
    "total": "Total"
  }
}
</i18n>
